import { useEffect, useMemo } from 'react';
import { Breadcrumb, Modal } from 'react-bootstrap';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiX } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { getBotWizardConfig } from './botWizardConfigs';
import renderBreadcrumbs from './components/renderBreadcrumbs';
import { resetCreateFlow, selectCreateModalState, selectCurrentDataState, selectCurrentTypeState, toggeleCreateModal } from './create.slice';
import { useWizardController } from './hooks/useWizardController';

function CreateWrapper() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const showCreateModal = useAppSelector(selectCreateModalState);
  const currentType = useAppSelector(selectCurrentTypeState);
  const currentData = useAppSelector(selectCurrentDataState);

  const cleanedData = useMemo(() => {
    if (currentType === BotTypeEnum.HUMAN_CLONE) return currentData;

    // Strip cloneExtra for non-clone flows
    const { cloneExtra, has_package, ...rest } = currentData || {};
    return { ...rest, has_package };
  }, [currentType, currentData]);

  const steps = useMemo(() => getBotWizardConfig(currentType, t, cleanedData), [currentType, t, cleanedData]);

  const { methods, Component, stepIndex, isFirstStep, isLastStep, next, back, props, isLoading } = useWizardController(steps);

  const handleClose = () => {
    dispatch(resetCreateFlow());
    dispatch(toggeleCreateModal(false));
    methods.reset();
  };

  useEffect(() => {
    if (methods.formState.errors && Object.keys(methods.formState.errors).length > 0) {
      toast.warn(t('General/key14'));
    }
  }, [methods.formState.errors]);

  if (!showCreateModal) return null;

  return (
    <Modal backdrop="static" centered show={showCreateModal} fullscreen>
      {showCreateModal && (
        <FormProvider {...methods}>
          <form
            onSubmit={e => {
              e.preventDefault();
              next();
            }}
            className="modal-content"
          >
            <Modal.Header className="tw-bg-gray-50 tw-border-gray-100 tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-flex">
              <ol className="md:tw-pl-7 breadcrumb tw-mb-0 tw-w-full tw-max-w-[95%] tw-overflow-auto tw-flex-nowrap [&>*]:tw-inline-flex">
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {currentType !== BotTypeEnum.HUMAN_CLONE ? t('Wizard/general/key3') : t('Wizard/general/key4')}
                </Breadcrumb.Item>
                {renderBreadcrumbs({
                  steps,
                  stepIndex,
                })}
              </ol>

              <BiX className="md:tw-mr-7 tw-w-6 tw-h-6 tw-relative tw-overflow-hidden tw-cursor-pointer" onClick={handleClose} />
            </Modal.Header>

            <Modal.Body className="tw-bg-gray-50 tw-h-full" id="botWizardContainer">
              {Component && <Component {...props} />}
            </Modal.Body>

            <Modal.Footer className="tw-bg-gray-50 tw-border-gray-100 md:tw-gap-0 tw-gap-14 tw-flex-row tw-items-end md:tw-pr-10">
              <div className="tw-flex tw-gap-3 md:tw-flex-nowrap tw-flex-wrap md:tw-w-max tw-justify-end">
                {/* Back Button */}
                {!isFirstStep && (
                  <PSButton
                    id="btn-create-back"
                    disabled={isLoading}
                    variant="secondary"
                    onClick={back}
                    type="button"
                    title={t('Wizard/general/key6')} // ← Back
                  />
                )}

                {/* Cancel Button on First Step */}
                {isFirstStep && (
                  <PSButton
                    id="btn-create-cancel"
                    disabled={isLoading}
                    variant="secondary"
                    onClick={handleClose}
                    type="button"
                    title={t('Wizard/general/key5')} // ← Cancel
                  />
                )}

                {/* Next or Submit Button */}
                <PSButton
                  id="btn-create-next"
                  disabled={isLoading || Object.keys(methods.formState.errors).length > 0}
                  isLoading={isLoading}
                  variant="primary"
                  type={'submit'}
                  title={
                    isLastStep && currentType === BotTypeEnum.MARKETPLACE
                      ? t('Wizard/general/key14')
                      : isLastStep && currentType !== BotTypeEnum.TEXT_SCENARIO && currentType !== BotTypeEnum.VIDEO_SCENARIO
                      ? t('Wizard/general/key7')
                      : t('Wizard/general/key9')
                  } // Submit / Next
                />
              </div>
            </Modal.Footer>
          </form>
        </FormProvider>
      )}
    </Modal>
  );
}

export default WrappedErrorBoundary(CreateWrapper);
